"use client";
import { redirect, useRouter, useSearchParams } from "next/navigation";
import SharedForm from "@/components/forms/shared-form";
import PrimaryButton from "@/components/ui/primary-button";
import ProgressBar from "@/components/ui/progress-bar";
import ButtonAssessment from "@/components/ui/button-assessment";
import { B2cType, useAssessmentStore } from "./assessment-store";
import { getFormInfo, getHeading } from "@/lib/get-page-info";
import { STEPS_BRANCHED_BY_CATEGORY_AND_TYPE } from "./constants";
import RoleForm from "@/components/forms/role-form";
import Stepper from "@/components/ui/stepper";
import Input from "@/components/ui/input";
import { ChangeEventHandler, useEffect, useTransition } from "react";
import React from "react";
import { createB2bLead, createB2cLead } from "./actions";
import { toast } from "sonner";
import { useHydration } from "../../../../hooks/use-hydration";
import LoadingAnimationWrapper from "./loading-animation-wrapper";
import { cn } from "@/lib/utils";
import LINKS from "@/lib/links";
import { sendGTMEvent } from "@next/third-parties/google";

export const assessmentFormInputStyles = cn(
  "gap-0 lg:grid lg:grid-cols-[150px_360px] lg:grid-rows-[minmax(min-content,max-content)_minmax(min-content,max-content)] lg:items-center lg:gap-x-4 lg:[&_div]:row-span-2 lg:[&_label:has(~_span.hidden)]:row-span-2 lg:[&_label]:text-right lg:[&_label]:leading-none lg:[&_span]:text-right lg:[&_span]:leading-none"
);

export default function AssessmentDynamic() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Pretty new hook: https://react.dev/reference/react/useTransition
  // I'm using this because we're using a server action to send data
  // and it does not have a loading hook 🤷‍♂️
  const [isPending, startTransition] = useTransition();
  // Getting from local storage takes a while...
  const isHydrated = useHydration();

  // Form control
  const step = useAssessmentStore((state) => state.step);
  const totalSteps = useAssessmentStore((state) => state.totalSteps);

  // Next button controller
  const step1Valid = useAssessmentStore((state) => state.step1Valid);
  const stepValid = useAssessmentStore((state) => state.stepValid);

  // is scheduling
  const isScheduling = useAssessmentStore((state) => state.isScheduling);
  const setIsScheduling = useAssessmentStore((state) => state.setIsScheduling);

  const createLead = () =>
    serviceCategory === "services-for-families"
      ? createB2cLead(submittedStep1Values, stepValues)
      : createB2bLead(submittedStep1Values, role, organization, stepValues, {
          cnaNum,
          medTechNum,
          lvnNum,
          rnNum,
          ptNum,
          otNum,
          officeStaffNum,
          pcNum,
        });
  const attemptASubmit = async () => {
    const error = await createLead();
    if (error) toast.error(error);
    setIsScheduling(true);
  };
  const handleNextButtonClick = () => {
    sendGTMEvent({
      event: "assessment_form_finished_step",
      step,
      serviceCategory,
    });
    if (step < totalSteps) {
      nextStep();
      if (serviceCategoryParams) router.replace(LINKS.careAssessment.href);
    } else {
      startTransition(() => attemptASubmit());
    }
  };
  const handlePrevButtonClick = () => {
    if (step > 1) prevStep();
    else router.back();
  };
  const nextStep = useAssessmentStore((state) => state.nextStep);
  const prevStep = useAssessmentStore((state) => state.prevStep);

  // Step 1 controller
  const serviceCategory = useAssessmentStore((state) => state.serviceCategory);
  const resetToStep1 = useAssessmentStore((state) => state.resetToStep1);

  // Role form controller
  const role = useAssessmentStore((state) => state.role);
  const organization = useAssessmentStore((state) => state.organization);

  // Staffing number controller
  const cnaNum = useAssessmentStore((state) => state.cnaNum);
  const changeCnaNum = useAssessmentStore((state) => state.changeCnaNum);
  const medTechNum = useAssessmentStore((state) => state.medTechNum);
  const changeMedTechNum = useAssessmentStore(
    (state) => state.changeMedTechNum
  );
  const lvnNum = useAssessmentStore((state) => state.lvnNum);
  const changeLvnNum = useAssessmentStore((state) => state.changeLvnNum);
  const rnNum = useAssessmentStore((state) => state.rnNum);
  const changeRnNum = useAssessmentStore((state) => state.changeRnNum);
  const ptNum = useAssessmentStore((state) => state.ptNum);
  const changePtNum = useAssessmentStore((state) => state.changePtNum);
  const otNum = useAssessmentStore((state) => state.otNum);
  const changeOtNum = useAssessmentStore((state) => state.changeOtNum);
  const officeStaffNum = useAssessmentStore((state) => state.officeStaffNum);
  const changeOfficeStaffNum = useAssessmentStore(
    (state) => state.changeOfficeStaffNum
  );
  const pcNum = useAssessmentStore((state) => state.pcNum);
  const changePcNum = useAssessmentStore((state) => state.changePcNum);

  // Step 2-9 controller
  const stepValues = useAssessmentStore((state) => state.stepValues);
  const staffRequired = stepValues[5];
  const submittedStep1Values = useAssessmentStore(
    (state) => state.submittedStep1Values
  );
  const toggleCheckboxOnStep = useAssessmentStore(
    (state) => state.toggleCheckboxOnStep
  );
  const toggleRadioOnStep = useAssessmentStore(
    (state) => state.toggleRadioOnStep
  );
  const toggleSelectAllOnStep = useAssessmentStore(
    (state) => state.toggleSelectAllOnStep
  );
  const addOtherValueOnStep = useAssessmentStore(
    (state) => state.addOtherValueOnStep
  );
  const handleOtherValueChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    addOtherValueOnStep(step, e.target.value);
  };
  const handleAssessmentButtonClick = (
    value: string,
    type: "radio" | "checkbox"
  ) => {
    if (value === "select-all") {
      toggleSelectAllOnStep(step);
      return;
    }
    if (type === "checkbox") {
      toggleCheckboxOnStep(step, value);
      return;
    }
    toggleRadioOnStep(step, value);
  };

  const b2cType =
    serviceCategory === "services-for-families"
      ? (stepValues[4] as B2cType)
      : undefined;
  const heading = getHeading(
    step,
    serviceCategory,
    STEPS_BRANCHED_BY_CATEGORY_AND_TYPE.includes(step) ? b2cType : undefined
  );
  const formInfo = getFormInfo(
    step,
    serviceCategory,
    STEPS_BRANCHED_BY_CATEGORY_AND_TYPE.includes(step) ? b2cType : undefined
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  const serviceCategoryParams = searchParams.get("category");

  useEffect(() => {
    if (serviceCategoryParams) {
      if (
        step > 1 &&
        (serviceCategoryParams === "services-for-families" ||
          serviceCategoryParams === "medical-staffing") &&
        serviceCategory !== serviceCategoryParams
      ) {
        resetToStep1();
      }
    }
  }, [resetToStep1, serviceCategory, serviceCategoryParams, step]);

  // Redirect to scheduling if finished but have not scheduled yet
  useEffect(() => {
    if (isScheduling) {
      redirect(LINKS.careAssessmentScheduling.href);
    }
  }, [isScheduling]);

  const hasOtherOrSelectAll = ({ value }: { value: string }) =>
    value === "other" || value === "select-all";

  const hasRoleFormOrStaffingForm = ({ type }: { type: string }) =>
    type === "role-form" || type === "staffing-form";

  const isNextButtonDisabled =
    (step === 1 && !step1Valid) || (step > 1 && !stepValid[step]);

  const isTwoColumns = formInfo.length > 7;
  const isOddLength = formInfo.length % 2;

  let buttonText = "";

  if (step < totalSteps) {
    buttonText = "Next Question";
  } else if (serviceCategory === "services-for-families") {
    buttonText = "Submit Care Assessment";
  } else {
    buttonText = "Submit Free Assessment";
  }

  return (
    <>
      {/* Too quick of a loading state, like less than half a second. Can be perceived as layout shift */}
      {/* I think it's worth considering adding 1.5 seconds of sleep */}
      {isPending || !isHydrated ? (
        <LoadingAnimationWrapper />
      ) : (
        <>
          <ProgressBar
            step={step}
            total={totalSteps}
            handlePrevButtonClick={handlePrevButtonClick}
          />
          <h1 className="text-body-1-500 text-center font-montserrat text-secondary-600 sm:text-subheading-2 lg:text-subheading-1">
            {heading}
          </h1>
          {step === 1 && (
            <SharedForm
              handleNextButtonClick={handleNextButtonClick}
              disabledButton={isNextButtonDisabled}
              buttonText={buttonText}
              router={router}
            />
          )}
          {step > 1 && (
            <form
              aria-label="who needs care form"
              className="flex-1"
            >
              <div
                className={cn(
                  "grid gap-6",
                  !formInfo.find(hasRoleFormOrStaffingForm) &&
                    (isTwoColumns ? "md:grid-cols-2" : "max-w-[300px]"),
                  formInfo.find(hasOtherOrSelectAll) &&
                    formInfo.length === 9 &&
                    "md:grid-flow-col md:grid-rows-4",
                  formInfo.find(hasOtherOrSelectAll) &&
                    formInfo.length === 11 &&
                    "md:grid-flow-col md:grid-rows-5",
                  formInfo.find(hasOtherOrSelectAll) &&
                    formInfo.length === 13 &&
                    "md:grid-flow-col md:grid-rows-6",
                  formInfo.find(hasOtherOrSelectAll) &&
                    formInfo.length === 15 &&
                    "md:grid-flow-col md:grid-rows-7"
                )}
              >
                {formInfo.map(
                  ({ value, text, type, radioGroup, description }) => {
                    const buttonOtherOrSelectAllInTwoColumns =
                      isTwoColumns &&
                      isOddLength &&
                      (value === "other" || value === "select-all");
                    if (["radio", "checkbox"].includes(type))
                      return (
                        <div
                          key={`step-${step}-${value}`}
                          className={cn(
                            // If it's a "other" or "select-all button" in a two-column list, force its position at the row end of the grid and take the entire row space. Inside, make a two-column grid to force the button to reside on the right
                            buttonOtherOrSelectAllInTwoColumns
                              ? "col-span-full space-y-6 md:-row-start-1 md:grid md:grid-cols-2 md:gap-6 md:space-y-0"
                              : "space-y-6"
                          )}
                        >
                          <ButtonAssessment
                            key={`step-${step}-choice-${value}`}
                            type={type as "radio" | "checkbox"}
                            name={radioGroup || text}
                            value={value}
                            description={description}
                            checked={stepValues[step].includes(value)}
                            className={cn(
                              isTwoColumns &&
                                isOddLength &&
                                value === "select-all" &&
                                "col-span-2",
                              isTwoColumns &&
                                isOddLength &&
                                value === "other" &&
                                "col-start-1"
                            )}
                            label={text}
                            onChange={(e) =>
                              handleAssessmentButtonClick(
                                e.target.value,
                                type as "radio" | "checkbox"
                              )
                            }
                          />
                          {value === "other" &&
                            !isTwoColumns &&
                            stepValues[step].includes("other") && (
                              <Input
                                key={`step-${step}-other-textbox`}
                                variant="rounded"
                                onChange={handleOtherValueChange}
                                disabled={!stepValues[step].includes("other")}
                                className={cn(isTwoColumns && "col-start-2")}
                                defaultValue={stepValues[step]
                                  .find((v) => v.includes("other:"))
                                  ?.slice(6)}
                              />
                            )}
                          {value === "other" &&
                            isTwoColumns &&
                            (stepValues[step].includes("other") ? (
                              <Input
                                key={`step-${step}-other-textbox`}
                                variant="rounded"
                                onChange={handleOtherValueChange}
                                className={isTwoColumns && "col-start-2"}
                                defaultValue={stepValues[step]
                                  .find((v) => v.includes("other:"))
                                  ?.slice(6)}
                              />
                            ) : (
                              <Input
                                key={`step-${step}-other-textbox-disabled`}
                                variant="rounded"
                                disabled
                                className={cn(
                                  isTwoColumns && "col-start-2",
                                  !isOddLength ? "hidden" : "hidden md:block"
                                )}
                                value=""
                              />
                            ))}
                        </div>
                      );
                    // Only one role form
                    if (type === "role-form")
                      return <RoleForm key={`step-role-form`} />;

                    // Render the stepper components dynamically based
                    // on the number of staff needed
                    if (type === "staffing-form") {
                      const multipleStaffNeeded = staffRequired.length > 1;
                      return (
                        <>
                          {staffRequired.includes(
                            "Certified Nursing Assistant (CNA)"
                          ) && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Certified Nursing Assistant (CNA)
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-cna-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={cnaNum}
                                onValueChange={changeCnaNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes("Medication Technician") && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Medication Technician
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-medtech-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={medTechNum}
                                onValueChange={changeMedTechNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes(
                            "Licensed Vocational Nurse (LVN)"
                          ) && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Licensed Vocational Nurse (LVN)
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-lvn-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={lvnNum}
                                onValueChange={changeLvnNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes("Registered Nurse (RN)") && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Registered Nurse (RN)
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-rn-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={rnNum}
                                onValueChange={changeRnNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes("Physical Therapist") && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Physical Therapist
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-pt-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={ptNum}
                                onValueChange={changePtNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes("Occupational Therapist") && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Occupational Therapist
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-ot-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={otNum}
                                onValueChange={changeOtNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes("Front Office Staff") && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Front Office Staff
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-office-staff-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={officeStaffNum}
                                onValueChange={changeOfficeStaffNum}
                              />
                            </div>
                          )}
                          {staffRequired.includes("Personal Concierge") && (
                            <div className="grid md:grid-cols-2 md:gap-8 lg:gap-10">
                              {multipleStaffNeeded && (
                                <label className="mb-2 text-center font-roboto text-xl font-light text-secondary-600 md:mb-0 md:flex md:flex-row md:items-center md:justify-end md:font-montserrat md:font-normal">
                                  Personal Concierge
                                </label>
                              )}
                              <Stepper
                                className={cn(
                                  !multipleStaffNeeded && "col-span-2"
                                )}
                                key="step-pc-stepper"
                                min={0}
                                max={100}
                                step={1}
                                defaultValue={pcNum}
                                onValueChange={changePcNum}
                              />
                            </div>
                          )}
                        </>
                      );
                    }
                  }
                )}
              </div>
              <div className={cn("mt-8")}>
                <PrimaryButton
                  id="assessment-form-button"
                  size="md"
                  color="orange"
                  onClick={handleNextButtonClick}
                  aria-label="next button"
                  disabled={isNextButtonDisabled}
                  className="w-full"
                  type="button"
                >
                  {buttonText}
                </PrimaryButton>
              </div>
            </form>
          )}
          {/* <div className="flex w-full flex-col justify-between gap-4 sm:flex-row">
            <PrimaryButton
              id="assessment-form-button"
              size="sm"
              color="orange"
              onClick={handleNextButtonClick}
              aria-label="next button"
              disabled={
                (step === 1 && !step1Valid) || (step > 1 && !stepValid[step])
              }
            >
              {buttonText}
            </PrimaryButton>
          </div> */}
        </>
      )}
    </>
  );
}
